<template>
    <Card :bordered="false" class="border-radius-0">
        <Row justify="center">
            <Col :sm="24" :lg="20" :xl="16" :xxl="12">
                <Form :ref="e => (refs.form = e)" layout="horizontal">
                    <TemplateBuilder :rowProp="data.rowProp" :useForm="state.useForm" :fields="state.fields"></TemplateBuilder>
                    <div class="text-center my-3">
                        <Button size="small" @click="onClickReset">{{ $t("button.reset") }}</Button>
                        <Button size="small" class="ml-2 btn-primary" @click="onClickSearch">{{ $t("button.search") }}</Button>
                    </div>
                </Form>
                <div v-if="data.searchModel?.refCalendarType?.key === 'Sampling Request'">
                    <pre class="text-danger">{{
                        $t("views.calendar.samplingRequest.remark", {
                            name: data.searchModel?.refDepartment?.nameTh,
                            limit: data.searchModel?.refDepartment?.attribute?.calendarLimitPerDay
                        })
                    }}</pre>
                </div>
                <FullCalendar :ref="e => (refs.fullCalendar = e)" :options="calendarOptions"></FullCalendar>
            </Col>
        </Row>
    </Card>
</template>

<script>
import fullcalendarlocaleTh from "@fullcalendar/core/locales/th";
import "@fullcalendar/core/vdom";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import FullCalendar from "@fullcalendar/vue3";
import { Button, Card, Form, Row, Col } from "ant-design-vue";
import lodash from "lodash";
import moment from "moment";
import { defineAsyncComponent, defineComponent, reactive } from "vue";
import { state as stateApp } from "../../../app.logic";
import { has } from "../../../utils/menu-context";
import { isLoading, onSearch, openModalForm, state } from "./calendar.logic";

export default defineComponent({
    components: {
        Card,
        Form,
        FullCalendar,
        Button,
        Row,
        Col,
        TemplateBuilder: defineAsyncComponent(() => import("../../../components/input/template-builder/TemplateBuilder"))
    },
    props: {
        menuContext: { default: () => stateApp.menuContext }
    },
    setup(props) {
        const data = reactive({ rowProp: { gutter: 24 }, searchFirst: false, searchModel: null });
        const refs = reactive({ form: null });
        const onClickSearch = () => {
            refs.fullCalendar.getApi().refetchEvents();
        };
        const onClickReset = () => {
            state.useForm.resetFields();
            onClickSearch();
        };
        return {
            state,
            props,
            data,
            refs,
            isLoading,
            onClickSearch,
            onClickReset,
            calendarOptions: {
                plugins: [dayGridPlugin, interactionPlugin],
                initialView: "dayGridMonth",
                locale: fullcalendarlocaleTh,
                dayMaxEventRows: 5,
                events: (fetchInfo, successCallback, failureCallback) => {
                    if (!data.searchFirst) {
                        data.searchFirst = true;
                        return;
                    }
                    const criteria = {
                        select: ["**"],
                        page: 0,
                        size: 1000000,
                        where: [
                            { name: "startDatetime", type: "Datetime", operator: ">=", value: fetchInfo.startStr },
                            { name: "endDatetime", type: "Datetime", operator: "<=", value: fetchInfo.endStr }
                        ]
                    };
                    const { refCalendarType, refDepartment } = state.useForm.modelRef;
                    if (refCalendarType) {
                        criteria.where.push({ name: "refCalendarType.id", type: "UUID", operator: "=", value: refCalendarType.id });
                    }
                    if (refDepartment) {
                        criteria.where.push({ name: "refDepartment.id", type: "UUID", operator: "=", value: refDepartment.id });
                    }
                    data.searchModel = lodash.cloneDeep(state.useForm.modelRef);
                    state.useForm
                        .validate()
                        .then(() => {
                            return onSearch({ page: criteria })
                                .then(page => {
                                    const events = page.data.map(d => ({
                                        id: d.id,
                                        allDay: d.isAllDay,
                                        start: moment(d.startDatetime).format(d.isAllDay ? "YYYY-MM-DD" : "YYYY-MM-DD[T]HH:mm:ssZ"),
                                        end: moment(d.endDatetime)
                                            .add(d.isAllDay ? 1 : 0, "days")
                                            .format(d.isAllDay ? "YYYY-MM-DD" : "YYYY-MM-DD[T]HH:mm:ssZ"),
                                        title: d.name,
                                        extendedProps: {
                                            sourceEvent: d
                                        }
                                    }));
                                    successCallback(events);
                                })
                                .catch(err => failureCallback(err));
                        })
                        .catch(error => {
                            successCallback([]);
                            throw error;
                        })
                        .finally(() => {
                            refs.fullCalendar.getApi().destroy();
                            refs.fullCalendar.getApi().render();
                        });
                },
                dateClick: info => {
                    if (!has({ menuContext: props.menuContext, key: "canCreate" })) {
                        return;
                    }
                    openModalForm({
                        props: {
                            menuContext: props.menuContext,
                            viewOnly: false,
                            setAppPageHeader: false,
                            form: {
                                isAllDay: info.allDay,
                                startDatetime: moment(info.date).format("YYYY-MM-DD[T]00:00:00Z"),
                                endDatetime: moment(info.date).format("YYYY-MM-DD[T]00:00:00Z"),
                                refCalendarType: data.searchModel?.refCalendarType,
                                refDepartment: data.searchModel?.refDepartment
                            },
                            visibleChangeLog: false
                        },
                        afterSaveCallback: onClickSearch
                    });
                },
                eventClick: info => {
                    openModalForm({
                        props: {
                            menuContext: props.menuContext,
                            viewOnly: !has({ menuContext: props.menuContext, key: "canEdit" }),
                            setAppPageHeader: false,
                            form: {
                                ...lodash.cloneDeep(info.event.extendedProps?.sourceEvent),
                                startDatetime: moment(info.event.extendedProps?.sourceEvent?.startDatetime).format("YYYY-MM-DD[T]HH:mm:ssZ"),
                                endDatetime: moment(info.event.extendedProps?.sourceEvent?.endDatetime).format("YYYY-MM-DD[T]HH:mm:ssZ")
                            },
                            visibleChangeLog: false
                        },
                        afterSaveCallback: onClickSearch
                    });
                },
                dayCellClassNames: args => {
                    if (data.searchModel?.refCalendarType?.key === "Sampling Request") {
                        const currentDateMoment = moment(args.date);
                        const { eventStore } = args.view.getCurrentData();
                        const events = Object.keys(eventStore.defs)
                            .map(key => eventStore.defs[key].extendedProps.sourceEvent)
                            .filter(
                                event => currentDateMoment.isSameOrAfter(event.startDatetime) && currentDateMoment.isSameOrBefore(event.endDatetime)
                            );
                        if (events.length >= data.searchModel?.refDepartment.attribute.calendarLimitPerDay) {
                            return "bg-ff6961";
                        }
                    }
                }
            }
        };
    }
});
</script>
